import { computed, effect, inject, Injectable, resource, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MetadataService } from '../metadata/metadata';
import { ReaderApi } from '../reader-api/reader-api';
import { ReaderNavigatorService } from '../readium/navigator/reader-navigator';
import { ToastService } from '../toast/toast';

@Injectable({
  providedIn: 'root',
})
export class TextInfoService {
  private static readonly DEFAULT_TARGET_LANGUAGE = 'en';
  private static readonly DEFAULT_TARGET_LANGUAGE_FALLBACK = 'es';
  private readonly readerApi = inject(ReaderApi);
  private readonly metadataService = inject(MetadataService);
  private readonly readerNavigatorService = inject(ReaderNavigatorService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  readonly selectionContext = this.readerNavigatorService.currentSelectionContext;
  readonly selectedText = signal<string>('');
  readonly keywordPhrase = computed(() => this.selectionContext()?.fullContext ?? '');
  readonly metadataInfo = this.metadataService.metadataInfo;

  readonly textInfoResource = resource({
    params: computed(() => this.selectedText().trim() || undefined),
    loader: async ({ params: text }) => {
      const body = {
        metadataInfo: this.metadataInfo(),
        keywordPhrase: this.keywordPhrase(),
        text,
      };
      return this.readerApi.getTextInfo(body);
    },
  });

  readonly translationFallbackResource = resource({
    params: computed(() => {
      const text = this.selectedText().trim();
      if (!text || this.textInfoResource.status() !== 'error') return undefined;
      return text;
    }),
    loader: async ({ params: text }) => {
      const preferredLanguage =
        this.metadataInfo().userLanguage ?? this.translateService.getCurrentLang();
      const translation = await this.readerApi.getTextTranslation({
        text,
        targetLanguage: preferredLanguage,
      });
      if (translation.detectedSourceLanguage !== preferredLanguage) {
        return { ...translation, oppositeLanguage: preferredLanguage };
      }

      const oppositeLanguage =
        preferredLanguage === TextInfoService.DEFAULT_TARGET_LANGUAGE
          ? TextInfoService.DEFAULT_TARGET_LANGUAGE_FALLBACK
          : TextInfoService.DEFAULT_TARGET_LANGUAGE;
      const retranslation = await this.readerApi.getTextTranslation({
        text,
        targetLanguage: oppositeLanguage,
        sourceLanguage: translation.detectedSourceLanguage,
      });
      return { ...retranslation, oppositeLanguage };
    },
  });

  constructor() {
    effect(() => {
      if (
        this.textInfoResource.status() === 'error' &&
        this.translationFallbackResource.status() === 'error'
      ) {
        this.toastService.error(String(this.translateService.instant('TEXT_INFO.ERROR_INFO')));
      }
    });
  }

  clearTextInfo(): void {
    this.selectedText.set('');
  }
}
